import { MarketPlaceType } from "@vahak/core/dist/constants/market";
import { getAllowedVehicleList } from "@vahak/core/dist/constants/lorryTypes";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";
import { SelectOption } from "@vahak/core-ui/dist/components/Select";

import {
    MarketPlacePageProps,
    parseMarketplaceLocationFromParamSegment
} from "../../../marketplace-new/marketplacePageHelpers";

export const MARKETPLACE_V3_ASSETS = {
    benefitCheckIcon: "/homepage-v2/icons/benefit-check.svg",
    noLoadAvailableImage: "/homepage-v2/no-load-available-banner.png"
} as const;

export const MARKETPLACE_V3_MOBILE_CAROUSEL = {
    cssMode: false,
    slidesPerView: 1,
    spaceBetween: 16,
    navigation: false,
    pagination: true
} as const;

export const MARKETPLACE_V3_LOADS_GRID = {
    desktopPageSize: 4,
    mobilePageSize: 3,
    viewMoreClicksBeforeLogin: 2
} as const;

export const MARKETPLACE_V3_LOADS_SECTION_ID = "marketplace-v3-loads";

/** Marketplace v3 marketing shell — load browse and lorry booking landing pages. */
export function keepsMarketingLayout(pathname: string) {
    return pathname.startsWith("/marketplace/load") || pathname.startsWith("/marketplace/lorry");
}

const toSlug = (label?: string) => label?.replace(/ /g, "-").toLowerCase();

/** Mirrors SSR parsing in `pages/marketplace/[...params].tsx`. */
export { parseMarketplaceLocationFromParamSegment } from "../../../marketplace-new/marketplacePageHelpers";

export function buildMarketplaceV3SearchUrl({
    srcState,
    destStates = [],
    lorryTypeIds = []
}: {
    srcState?: SelectOption<number>;
    destStates?: SelectOption<number>[];
    lorryTypeIds?: number[];
}) {
    const srcSlug = toSlug(srcState?.label);
    const destSlug = toSlug(destStates.filter((state) => state.id !== -1)[0]?.label);
    const path = ROUTES.marketplace.getUrl(MarketPlaceType.LOAD, srcSlug, destSlug);
    const searchParams = new URLSearchParams();

    if (lorryTypeIds.length) {
        getAllowedVehicleList("lorry")
            .filter((lorryType) => lorryTypeIds.includes(lorryType.id))
            .forEach((lorryType) => searchParams.append("l", lorryType.label.toLowerCase()));
    }

    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}` : path;
}

/** Modal/query params that must not trigger marketplace URL re-hydration. */
const MARKETPLACE_V3_TRANSIENT_QUERY_PARAMS = ["lorry-posting-form", "load-posting-form"] as const;

/**
 * Marketplace path + filter query only. Strips hash and posting-form modal params so
 * opening load/lorry posting does not re-run URL hydration (which would wipe the modal query).
 */
export function getMarketplaceV3UrlKey(asPath: string) {
    const pathWithoutHash = asPath.split("#")[0];
    const [pathname, query = ""] = pathWithoutHash.split("?");
    const searchParams = new URLSearchParams(query);

    MARKETPLACE_V3_TRANSIENT_QUERY_PARAMS.forEach((param) => searchParams.delete(param));

    const queryString = searchParams.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
}

export type MarketplaceV3Props = MarketPlacePageProps;
